import React, { useState } from 'react'

import ColumnLegendButton from './ColumnLegendButton'
import {
  formatGT,
  formatVafMode,
  infoFlag,
  infoNumber,
  isCnv,
  lohInterpretation,
  sampleVaf,
  tagBadgeClass,
  variantLocus,
  variantTag,
  type SampleData,
  type Variant,
} from './types'

export type VariantDetailPanelProps = {
  variant: Variant | null
  onOpenInIgv?: (locus: string) => void
}

function MetaRow({ label, value, title }: { label: string; value: React.ReactNode; title?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1" title={title}>
      <span className="text-text-subtle text-xs">{label}</span>
      <span className="text-text-heading font-mono text-sm tabular-nums">{value}</span>
    </div>
  )
}

function formatVafCard(sample: SampleData): string {
  const vaf = sampleVaf(sample)
  return vaf === null ? '-' : vaf.toFixed(3)
}

function depthArray(value: unknown): string {
  return Array.isArray(value) ? value.join(', ') : '-'
}

function SampleCard({ name, sample }: { name: string; sample: SampleData }) {
  const dp = typeof sample.DP === 'number' ? sample.DP : null
  const gq = typeof sample.GQ === 'number' ? sample.GQ : null
  const hasHybrid = sample['SAD'] !== undefined || sample['LAD'] !== undefined
  return (
    <div className="border-border-subtle rounded-lg border p-3" data-testid="detail-sample-card">
      <p className="text-text-heading mb-1 truncate font-mono text-xs font-semibold" title={name}>
        {name}
      </p>
      <MetaRow label="GT" value={formatGT(sample.GT)} title="Genotype" />
      <MetaRow label="VAF" value={formatVafCard(sample)} title="Variant allele fraction" />
      <MetaRow label="DP" value={dp === null ? '-' : dp} title="Read depth" />
      <MetaRow label="GQ" value={gq === null ? '-' : gq} title="Genotype quality" />
      <MetaRow label="AD" value={sample.AD ? sample.AD.join(', ') : '-'} title="Allelic depths (ref, alt)" />
      {hasHybrid && (
        <>
          <MetaRow label="SR AD" value={depthArray(sample['SAD'])} title="Short-read allele depth (ref, alt)" />
          <MetaRow label="LR AD" value={depthArray(sample['LAD'])} title="Long-read allele depth (ref, alt)" />
        </>
      )}
    </div>
  )
}

function AnnotationsSection({ variant }: { variant: Variant }) {
  const tag = variantTag(variant)
  const mlProb = infoNumber(variant, 'ML_PROB')
  const gnomad = infoNumber(variant, 'AF_genomes')
  const v20 = infoNumber(variant, 'AF_v20')
  const isStr = infoFlag(variant, 'STR')
  // Nothing to show for VCFs without the annotation pipeline's fields.
  if (tag === null && mlProb === null && gnomad === null && v20 === null && !isStr) return null
  return (
    <section className="border-border-subtle border-b p-4" data-testid="detail-annotations">
      <h3 className="text-text-subtle mb-2 text-xs font-semibold tracking-wide uppercase">Annotations</h3>
      {tag !== null && (
        <div className="flex items-baseline justify-between gap-3 py-1">
          <span className="text-text-subtle text-xs">TAG</span>
          <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${tagBadgeClass(tag)}`}>{tag}</span>
        </div>
      )}
      {mlProb !== null && <MetaRow label="ML_PROB" value={mlProb.toFixed(3)} title="ML reject probability" />}
      {gnomad !== null && <MetaRow label="gnomAD AF" value={gnomad.toPrecision(3)} title="gnomAD genomes AF" />}
      {v20 !== null && <MetaRow label="v20 AF" value={v20.toPrecision(3)} title="v20 panel allele frequency" />}
      {isStr && <MetaRow label="STR" value="yes" title="Short tandem repeat" />}
    </section>
  )
}

function CnvSection({ variant }: { variant: Variant }) {
  if (!isCnv(variant)) return null
  const depth = variant.depth ?? null
  // Expected band as a readable range: "0.40 - 0.60", or ">= 1.80" when unbounded.
  const band = depth
    ? depth.expected_max === null
      ? `>= ${depth.expected_min.toFixed(2)}`
      : `${depth.expected_min.toFixed(2)} - ${depth.expected_max.toFixed(2)}`
    : '-'
  const bandBadge = depth
    ? depth.in_expected_band
      ? 'bg-green-100 text-green-800'
      : 'bg-amber-100 text-amber-800'
    : ''
  return (
    <section className="border-border-subtle border-b p-4" data-testid="detail-cnv">
      <h3 className="text-text-subtle mb-2 text-xs font-semibold tracking-wide uppercase">CNV</h3>
      {typeof variant.cn === 'number' && <MetaRow label="Copy number" value={variant.cn} title="INFO/CN" />}
      {variant.svtype && <MetaRow label="Type" value={variant.svtype} title="INFO/SVTYPE" />}
      {typeof variant.svlen === 'number' && (
        <MetaRow
          label="Length"
          value={`${Math.abs(variant.svlen).toLocaleString()} bp`}
          title="Span from INFO/SVLEN (absolute); deletions carry a negative SVLEN."
        />
      )}
      {typeof variant.end === 'number' && (
        <MetaRow
          label="Interval"
          value={`${variant.chrom}:${variant.pos.toLocaleString()}-${variant.end.toLocaleString()}`}
        />
      )}
      {depth && (
        <>
          <div
            className="flex items-baseline justify-between gap-3 py-1"
            title="Normalized depth ratio (DPS) vs the expected band for the called CN"
          >
            <span className="text-text-subtle text-xs">Depth ratio</span>
            <span className="flex items-center gap-1.5">
              <span className="text-text-heading font-mono text-sm tabular-nums">{depth.ratio.toFixed(2)}</span>
              <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${bandBadge}`}>
                {depth.in_expected_band ? 'in band' : 'out of band'}
              </span>
            </span>
          </div>
          <MetaRow label="Expected" value={band} title={depth.band_label} />
          {depth.ratio_mapq0 !== null && (
            <MetaRow
              label="Depth ratio (MQ>0)"
              value={depth.ratio_mapq0.toFixed(2)}
              title="Same ratio from non-zero mapping-quality reads (DP0S)"
            />
          )}
          {depth.mappability_flag && (
            <div className="flex items-baseline justify-between gap-3 py-1">
              <span className="text-text-subtle text-xs">Mappability</span>
              <span
                className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800"
                title="All-reads depth is elevated but non-zero-MQ depth collapses (paralog/segdup fingerprint). Cross-check the segmental-dups track."
              >
                paralog/segdup suspect
              </span>
            </div>
          )}
        </>
      )}
      <LohBlock variant={variant} />
      <VafModeBlock variant={variant} />
    </section>
  )
}

function LohBlock({ variant }: { variant: Variant }) {
  const m = variant.review_metrics ?? null
  if (!m) return null
  const interp = lohInterpretation(m)
  return (
    <div className="border-border-subtle mt-2 border-t pt-2" data-testid="detail-loh">
      <div
        className="flex items-baseline justify-between gap-3 py-1"
        title="Heterozygous fraction of alt-bearing SNVs in the interval (from the sample's SNV track). Low under a true loss, near 1 in a balanced diploid region."
      >
        <span className="text-text-subtle text-xs">Het fraction</span>
        <span className="flex items-center gap-1.5">
          <span className="text-text-heading font-mono text-sm tabular-nums">
            {m.het_frac === null ? '-' : m.het_frac.toFixed(2)}
          </span>
          {interp && (
            <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${interp.badgeClass}`}>{interp.label}</span>
          )}
        </span>
      </div>
      <MetaRow
        label="Het / hom-alt"
        value={`${m.n_het} / ${m.n_hom_alt}`}
        title="Heterozygous vs homozygous-alt SNV sites in the interval"
      />
      <MetaRow
        label="Balanced hets"
        value={m.n_balanced_het}
        title="Hets whose alt fraction sits in the balanced 0.30-0.70 band"
      />
      <MetaRow label="SNVs scanned" value={m.n_snv} title="Fully-called SNV sites in the interval" />
    </div>
  )
}

function pct(frac: number | null): string {
  return frac === null ? '-' : `${Math.round(frac * 100)}%`
}

function VafModeBlock({ variant }: { variant: Variant }) {
  const v = variant.review_metrics?.vaf_mode ?? null
  if (!v) return null
  const modes = v.expected_modes.map(formatVafMode).join(', ')
  return (
    <div className="border-border-subtle mt-2 border-t pt-2" data-testid="detail-vaf-mode">
      <div
        className="flex items-baseline justify-between gap-3 py-1"
        title="For a gain, germline het VAFs are expected to shift off 1/2 toward the copy-number modes (CN=3 near 1/3 and 2/3; CN>=4 near 1/4 and 3/4). Shares below are advisory: the signal is noisy and diploid VAFs are reference-biased."
      >
        <span className="text-text-subtle text-xs">VAF modes (gain)</span>
        <span className="text-text-heading font-mono text-sm tabular-nums">{modes || '-'}</span>
      </div>
      <MetaRow
        label="At expected modes"
        value={`${pct(v.frac_at_expected_modes)} (${v.n_at_expected_modes}/${v.n_het_vaf})`}
        title="Share of usable het VAFs sitting at the CN-expected off-center modes"
      />
      <MetaRow
        label="Near 1/2 (diploid)"
        value={`${pct(v.frac_diploid)} (${v.n_diploid}/${v.n_het_vaf})`}
        title="Share of usable het VAFs still near the diploid 0.5 center"
      />
      <MetaRow
        label="Het VAFs scanned"
        value={v.n_het_vaf}
        title="Heterozygous sites with a usable VAF in the interval"
      />
    </div>
  )
}

const VariantDetailPanel: React.FC<VariantDetailPanelProps> = ({ variant, onOpenInIgv }) => {
  const [rawOpen, setRawOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  if (!variant) {
    return (
      <div
        className="text-text-subtle flex h-full items-center justify-center p-6 text-center text-sm"
        data-testid="detail-empty"
      >
        Select a variant to see its full breakdown.
      </div>
    )
  }

  const locus = variantLocus(variant)
  const copyLocus = () => {
    void navigator.clipboard?.writeText(locus).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1200)
    })
  }

  return (
    <div className="flex min-h-0 flex-col" data-testid="variant-detail-panel">
      <header className="border-border-subtle border-b p-4">
        <div className="flex items-center gap-2">
          <h2 className="text-text-heading font-mono text-lg font-semibold">{locus}</h2>
          <button
            type="button"
            onClick={copyLocus}
            title="Copy locus"
            className="text-text-subtle hover:text-text-heading text-xs"
          >
            {copied ? 'copied' : 'copy'}
          </button>
          {onOpenInIgv && (
            <button
              type="button"
              onClick={() => onOpenInIgv(locus)}
              className="text-brand-700 ml-auto text-xs hover:underline"
              data-testid="detail-open-igv"
            >
              Open in IGV
            </button>
          )}
        </div>
        <p className="text-text-subtle mt-1 font-mono text-sm">
          {variant.ref} {'>'} {variant.alt.join(',')}
          {variant.rsid ? ` · ${variant.rsid}` : ''}
        </p>
      </header>

      <AnnotationsSection variant={variant} />

      <CnvSection variant={variant} />

      <section className="border-border-subtle border-b p-4">
        <h3 className="text-text-subtle mb-2 text-xs font-semibold tracking-wide uppercase">Quality</h3>
        <MetaRow label="QUAL" value={variant.qual === null ? '-' : variant.qual.toFixed(2)} />
        <MetaRow label="FILTER" value={variant.filter.length ? variant.filter.join(', ') : '-'} />
      </section>

      <section className="border-border-subtle border-b p-4">
        <div className="mb-2 flex items-center gap-1.5">
          <h3 className="text-text-subtle text-xs font-semibold tracking-wide uppercase">
            Samples ({Object.keys(variant.samples).length})
          </h3>
          <ColumnLegendButton
            heading="VAF vs the IGV pileup"
            ariaLabel="How VAF relates to the IGV pileup"
            title="Why can VAF differ from the IGV reads?"
            testId="vaf-help"
            labelWidth={40}
            items={[
              {
                label: 'VAF',
                help: "The caller's allele fraction (AD/AF from the VCF) and is authoritative. The IGV pileup is a raw view: faint reads are low mapping-quality (usually excluded by the caller) and dense regions are downsampled, so it can look different.",
              },
            ]}
            footer="Compare against the matching short-read or long-read track."
          />
        </div>
        <div className="grid gap-2">
          {Object.entries(variant.samples).map(([name, sample]) => (
            <SampleCard key={name} name={name} sample={sample} />
          ))}
        </div>
      </section>

      <section className="p-4">
        <button
          type="button"
          onClick={() => setRawOpen((o) => !o)}
          className="text-text-subtle hover:text-text-heading text-xs font-semibold tracking-wide uppercase"
          data-testid="detail-raw-toggle"
        >
          {rawOpen ? '▼' : '▶'} Raw INFO / FORMAT
        </button>
        {rawOpen && (
          <pre className="mt-2 max-h-72 overflow-auto rounded bg-gray-50 p-2 text-[11px] leading-relaxed">
            {JSON.stringify({ info: variant.info, samples: variant.samples }, null, 2)}
          </pre>
        )}
      </section>
    </div>
  )
}

export default VariantDetailPanel
